import "./styles/layers.css";
import "./styles/portal.css";
import "@mantine/core/styles.layer.css";
import "@mantine/charts/styles.layer.css";
import "@mantine/dates/styles.layer.css";
import "@xplatform/design-system/styles.layer.css";
import i18n, { I18N_NAMESPACE } from "@config/i18n";
import { Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { injectStylesheet } from "@utils/injectStyles";
import { AdminContextProvider } from "@src/ContextProvider";
import { LoadingScreen, XPlatformProvider } from "@xplatform/design-system";
import { PermissionGuard } from "@components/PermissionGuard";
import { AdminPermissions } from "@config/permissions";
import AdminPage from "@pages/AdminPage";
import { AdminLandingRedirect } from "@pages/AdminLandingRedirect";
import DashboardPage from "@pages/DashboardPage/DashboardPage";
import { DoctypeManagementPage } from "@pages/DoctypeManagementPage";
import { DocTypeFlowPage } from "@pages/DoctypeManagementPage/DocTypeFlowPage";
import { CategoryManagementPage } from "@pages/CategoryManagementPage";
import { MonitoringPage } from "@pages/MonitoringPage";
import { ProcessDetailPage } from "@pages/MonitoringPage/ProcessDetailPage";
import { MappingJobDetailPage } from "@pages/MonitoringPage/MappingJobDetailPage";
import { TransactionDetailPage } from "@pages/MonitoringPage/TransactionDetailPage";
import { OrganisationDetailPage, OrganisationsPage } from "@pages/OrganisationsPage";
import { ModelsPage } from "@pages/ModelsPage";
import { ProviderManagementPage } from "@pages/ProviderManagementPage";
import {
    MappingConfigDetailPage,
    MappingConfigEditPage,
    MappingConfigListPage,
    MappingConfigReviewPage,
} from "@pages/MappingConfigPage";
import { ApiKeysPage, AuditPage, ModulesPage, PermissionsPage, RolesPage, UsersPage } from "@pages/AccessPage";
import PermissionErrorPage from "@pages/PermissionErrorPage/PermissionErrorPage";
import { I18nextProvider } from "react-i18next";

const PORTAL_ID = "admin-portal-root";

let cssStatus: "pending" | "ready" = "pending";
const cssPromise = injectStylesheet(
    `${import.meta.env.BASE_URL || "/"}assets/style.css`,
).then(() => {
    cssStatus = "ready";
});

function useModuleStylesheet() {
    if (cssStatus === "pending") throw cssPromise;
}

const AdminApp = () => {
    useModuleStylesheet();

    return (
        // Scopes every bare useTranslation() in this module to the
        // `admin` namespace. Module keys can no longer collide with
        // another module's in the shared `translation` namespace, which
        // the shell and BeeHive keep using.
        <I18nextProvider i18n={i18n} defaultNS={I18N_NAMESPACE}>
            <XPlatformProvider>
                <AdminContextProvider>
                    <Suspense fallback={<LoadingScreen message="Loading..." />}>
                        <Routes>
                            <Route element={<AdminPage />}>
                                <Route index element={<AdminLandingRedirect />} />
                                <Route
                                    path="dashboard"
                                    element={
                                        <PermissionGuard
                                            requiresAny={[
                                                AdminPermissions.DASHBOARD_VIEW,
                                            ]}
                                        >
                                            <DashboardPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="doctypes"
                                    element={
                                        <PermissionGuard
                                            requiresAny={[
                                                AdminPermissions.DOCTYPE_VIEW,
                                                AdminPermissions.DOCTYPE_MANAGE,
                                            ]}
                                        >
                                            <DoctypeManagementPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="doctypes/:docTypeId/flows"
                                    element={
                                        <PermissionGuard
                                            requiresAny={[
                                                AdminPermissions.DOCTYPE_MANAGE,
                                            ]}
                                        >
                                            <DocTypeFlowPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="categories"
                                    element={
                                        <PermissionGuard
                                            requiresAny={[
                                                AdminPermissions.CATEGORY_VIEW,
                                                AdminPermissions.CATEGORY_MANAGE,
                                            ]}
                                        >
                                            <CategoryManagementPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="monitoring"
                                    element={
                                        <PermissionGuard
                                            requiresAny={[
                                                AdminPermissions.MONITORING_VIEW,
                                            ]}
                                        >
                                            <MonitoringPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="monitoring/transactions/:groupId"
                                    element={
                                        <PermissionGuard
                                            requiresAny={[
                                                AdminPermissions.MONITORING_VIEW,
                                            ]}
                                        >
                                            <TransactionDetailPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="monitoring/process/:processId"
                                    element={
                                        <PermissionGuard
                                            requiresAny={[
                                                AdminPermissions.MONITORING_VIEW,
                                            ]}
                                        >
                                            <ProcessDetailPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="monitoring/mapping-job/:jobId"
                                    element={
                                        <PermissionGuard
                                            requiresAny={[
                                                AdminPermissions.MONITORING_VIEW,
                                            ]}
                                        >
                                            <MappingJobDetailPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="providers"
                                    element={
                                        <PermissionGuard
                                            requiresAny={[
                                                AdminPermissions.PROVIDER_VIEW,
                                            ]}
                                        >
                                            <ProviderManagementPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="organisations"
                                    element={
                                        <PermissionGuard
                                            requiresAny={[
                                                AdminPermissions.MONITORING_VIEW_ALL,
                                            ]}
                                        >
                                            <OrganisationsPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="organisations/:orgId"
                                    element={
                                        <PermissionGuard
                                            requiresAny={[
                                                AdminPermissions.DASHBOARD_VIEW,
                                            ]}
                                        >
                                            <OrganisationDetailPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="models"
                                    element={
                                        <PermissionGuard
                                            requiresAny={[
                                                AdminPermissions.PROVIDER_VIEW,
                                            ]}
                                        >
                                            <ModelsPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="mapping-configs"
                                    element={
                                        <PermissionGuard requiresAny={[AdminPermissions.MAPPING_CONFIG_VIEW]}>
                                            <MappingConfigListPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="mapping-configs/:service/:pluginId"
                                    element={
                                        <PermissionGuard requiresAny={[AdminPermissions.MAPPING_CONFIG_VIEW]}>
                                            <MappingConfigDetailPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="mapping-configs/:service/:pluginId/versions/:versionId/edit"
                                    element={
                                        <PermissionGuard
                                            requires={[
                                                AdminPermissions.MAPPING_CONFIG_VIEW,
                                                AdminPermissions.MAPPING_CONFIG_MANAGE,
                                            ]}
                                        >
                                            <MappingConfigEditPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="mapping-configs/:service/:pluginId/versions/:versionId/review"
                                    element={
                                        <PermissionGuard requiresAny={[AdminPermissions.MAPPING_CONFIG_VIEW]}>
                                            <MappingConfigReviewPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="access/users"
                                    element={
                                        <PermissionGuard
                                            requires={[
                                                AdminPermissions.ACCESS,
                                                AdminPermissions.ACCESS_VIEW,
                                            ]}
                                        >
                                            <UsersPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="access/roles"
                                    element={
                                        <PermissionGuard
                                            requires={[
                                                AdminPermissions.ACCESS,
                                                AdminPermissions.ACCESS_VIEW,
                                            ]}
                                        >
                                            <RolesPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="access/modules"
                                    element={
                                        <PermissionGuard
                                            requires={[
                                                AdminPermissions.ACCESS,
                                                AdminPermissions.ACCESS_VIEW,
                                            ]}
                                        >
                                            <ModulesPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="access/permissions"
                                    element={
                                        <PermissionGuard
                                            requires={[
                                                AdminPermissions.ACCESS,
                                                AdminPermissions.ACCESS_VIEW,
                                            ]}
                                        >
                                            <PermissionsPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="access/api-keys"
                                    element={
                                        <PermissionGuard
                                            requires={[
                                                AdminPermissions.ACCESS,
                                                AdminPermissions.ACCESS_VIEW,
                                            ]}
                                        >
                                            <ApiKeysPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="access/audit"
                                    element={
                                        <PermissionGuard
                                            requires={[
                                                AdminPermissions.ACCESS,
                                                AdminPermissions.ACCESS_VIEW,
                                            ]}
                                        >
                                            <AuditPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route path="permission-error" element={<PermissionErrorPage />} />
                            </Route>
                        </Routes>
                    </Suspense>
                </AdminContextProvider>
                <div
                    id={PORTAL_ID}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        pointerEvents: "none",
                        zIndex: 9999,
                    }}
                />
            </XPlatformProvider>
        </I18nextProvider>
    );
};

export default AdminApp;
